import { useState, useEffect, useCallback } from 'react';
import { X, Loader2, CalendarClock, ClipboardList, Flag, MailCheck, Mail, Phone, Check, Ban, FileDown } from 'lucide-react';
import { arDunningApi } from '../../services/arDunningApi';
import { useToast } from '../ui/Toast';
import { ArCommentsThread } from './ArCommentsThread';
import { ActionModal } from './ActionModal';
import { SendReminderModal } from './SendReminderModal';
import { RiskBadge } from './RiskBadge';
// P4 of the customer-duplicates plan: a read-only hint that this customer is carried on more
// accounts, in other companies, with a group-wide balance. It renders nothing unless the viewer
// may open /customer-duplicates and the customer is actually in a group. Nothing about the AR
// calculation changes.
import { CustomerGroupLink } from '../customer-duplicates/CustomerGroupLink';
import { formatMoney, formatDate, formatDateTime, agingClass, bucketLabel, levelBadgeClass, formatDelayDays } from '../../utils/arDunningFormat';
import type { ArOpenItem, ArAction, ArActionType, ArActionStatus, ArCompanyOption, ArCustomerRisk } from '../../types/arDunning';

interface Props {
    item: ArOpenItem;
    canEdit: boolean;
    companies: ArCompanyOption[];
    /** The customer's payment-behaviour risk (from the page's risk map). */
    risk?: ArCustomerRisk;
    onClose: () => void;
    /** Fired after any action/comment change so the list can re-fetch. */
    onChanged: () => void;
}

const ACTION_ICON: Record<ArActionType, typeof CalendarClock> = {
    PromiseToPay: CalendarClock,
    FollowUp: ClipboardList,
    Dispute: Flag,
    ReminderSent: MailCheck,
    Escalation: MailCheck,
    Call: Phone,
};

const CLOSABLE_STATUSES: Record<string, ArActionStatus[]> = {
    PromiseToPay: ['Kept', 'Broken', 'Cancelled'],
    FollowUp: ['Done', 'Cancelled'],
    Dispute: ['Done', 'Cancelled'],
};

/**
 * Left-click drill-down: right-hand side panel with the item summary, its dunning
 * action history (with status resolution), and the comment thread. Quick-action
 * buttons open the ActionModal. Rendered at z-50; the ActionModal stacks above it.
 */
export function ItemDetailPanel({ item, canEdit, companies, risk, onClose, onChanged }: Props) {
    const { toast } = useToast();
    const [actions, setActions] = useState<ArAction[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAction, setShowAction] = useState<ArActionType | null>(null);
    const [sendMode, setSendMode] = useState<'per-customer' | 'combined' | null>(null);
    const [pdfBusy, setPdfBusy] = useState(false);
    // Issued RiBa is collected by the bank (SBF) and is never dunned — reflected in the maturity
    // display and by hiding the reminder actions.
    const isRiba = item.ItemCategory === 'RibaIssued';

    const downloadStatement = async () => {
        setPdfBusy(true);
        try {
            await arDunningApi.downloadStatement(item.CustomerID, item.CompanyCode);
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to generate statement', 'error');
        } finally {
            setPdfBusy(false);
        }
    };

    const loadActions = useCallback(async () => {
        setLoading(true);
        try {
            const res = await arDunningApi.listActionsForEntity('Invoice', item.ItemKey);
            setActions(res.actions);
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to load actions', 'error');
        } finally {
            setLoading(false);
        }
    }, [item.ItemKey, toast]);

    useEffect(() => { void loadActions(); }, [loadActions]);

    // Esc closes the panel (unless a modal is open — it owns Esc then).
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !showAction && !sendMode) onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose, showAction, sendMode]);

    const resolveAction = async (a: ArAction, status: ArActionStatus) => {
        try {
            await arDunningApi.updateAction(a.Id, { status });
            await loadActions();
            onChanged();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to update action', 'error');
        }
    };

    const afterAction = () => { void loadActions(); onChanged(); };

    return (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
            <div className="flex h-full w-full max-w-lg flex-col bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-start justify-between border-b border-slate-200 p-4">
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2 text-base font-semibold text-slate-800">
                            {item.InvoiceNo}
                            {item.DocumentType === 'CreditMemo' && <span className="rounded bg-emerald-50 px-1 text-xs text-emerald-700">CM</span>}
                            {item.ItemCategory === 'RibaIssued' && <span title="Issued RiBa — collected by the bank (SBF)" className="rounded bg-blue-50 px-1.5 text-xs font-medium text-blue-700">RiBa (SBF)</span>}
                            {item.ItemCategory === 'Dishonored' && <span title="Dishonored RiBa (insoluto)" className="rounded bg-rose-100 px-1.5 text-xs font-semibold text-rose-700">Insoluto</span>}
                            {item.IsDisputed && <span className="rounded bg-amber-100 px-1.5 text-xs font-medium text-amber-800">Disputed</span>}
                        </div>
                        <div className="mt-0.5 truncate text-sm text-slate-500" title={item.CustomerName ?? ''}>{item.CustomerName ?? item.CustomerCode}</div>
                        <CustomerGroupLink customerId={item.CustomerID} className="mt-1" />
                    </div>
                    <button onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                    {/* Summary grid */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                        <Field label="Company">{item.CompanyCode.toUpperCase()} <span className="text-xs text-slate-400">({item.Source})</span></Field>
                        <Field label="Open amount"><span className="font-medium text-slate-800">{formatMoney(item.OpenAmountEUR)}</span>{item.Currency !== 'EUR' && <span className="ml-1 text-xs text-slate-400">{formatMoney(item.OpenAmountFCY, item.Currency)}</span>}</Field>
                        <Field label="Invoice date">{formatDate(item.InvoiceDate)}</Field>
                        <Field label="Due date">{formatDate(item.DueDate)}</Field>
                        {isRiba ? (
                            <Field label="Maturity"><span className="text-blue-700">{item.DaysOverdue > 0 ? `matured ${item.DaysOverdue}d ago` : item.DaysOverdue < 0 ? `matures in ${-item.DaysOverdue}d` : 'matures today'} · bank collection</span></Field>
                        ) : (
                            <Field label="Aging"><span className={agingClass(item.AgingBucket)}>{item.DaysOverdue > 0 ? `${item.DaysOverdue}d · ` : ''}{bucketLabel(item.AgingBucket)}</span></Field>
                        )}
                        <Field label="Sales manager">{item.SalesManagerName ?? '–'}</Field>
                        <Field label="Reminder level">
                            <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${levelBadgeClass(item.LastReminderLevel ?? 0)}`}>{item.LastReminderLevel ? `L${item.LastReminderLevel}` : 'none'}</span>
                            {item.LastReminderAt && <span className="ml-1 text-xs text-slate-400">{formatDate(item.LastReminderAt)}</span>}
                        </Field>
                        <Field label="Suggested next">{item.NextLevel > 0 ? <span className="font-medium text-rose-700">L{item.NextLevel}</span> : <span className="text-slate-400">–</span>}</Field>
                        {item.HasOpenPromise && <Field label="Promise"><span className="text-sky-700">to pay by {formatDate(item.PromiseDate)}</span></Field>}
                        <Field label="Payment risk (customer)" className="col-span-2">
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <RiskBadge risk={risk} variant="pill" />
                                {risk?.AvgDelayDays != null && <span className="text-xs text-slate-400">pays avg {formatDelayDays(risk.AvgDelayDays)} · {risk.PaidInvoiceCount} paid</span>}
                            </div>
                            {risk && risk.RiskReasons.length > 0 && (
                                <div className="mt-1 text-xs text-slate-500">{risk.RiskReasons.join(' · ')}</div>
                            )}
                        </Field>
                    </div>

                    {/* Account statement (available to view-only users; opens as a PDF download) */}
                    <div className="mt-4 border-t border-slate-100 pt-4">
                        <button
                            onClick={downloadStatement}
                            disabled={pdfBusy}
                            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                        >
                            {pdfBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} Statement PDF
                        </button>
                        <span className="ml-2 text-xs text-slate-400">All open items for {item.CustomerName ?? item.CustomerCode}</span>
                    </div>

                    {/* Quick actions */}
                    {canEdit && (
                        <div className="mt-4 border-t border-slate-100 pt-4">
                            {isRiba && (
                                <p className="mb-2 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs text-blue-800">
                                    Issued RiBa — collected by the bank (SBF). Reminders are disabled for this item.
                                </p>
                            )}
                            <div className="flex flex-wrap gap-2">
                                {!isRiba && <QuickBtn icon={MailCheck} label="Mark reminded" onClick={() => setShowAction('ReminderSent')} />}
                                <QuickBtn icon={CalendarClock} label="Promise" onClick={() => setShowAction('PromiseToPay')} />
                                <QuickBtn icon={ClipboardList} label="Follow-up" onClick={() => setShowAction('FollowUp')} />
                                <QuickBtn icon={Phone} label="Call" onClick={() => setShowAction('Call')} />
                                <QuickBtn icon={Flag} label="Dispute" onClick={() => setShowAction('Dispute')} />
                                {!isRiba && (
                                    <button
                                        onClick={() => setSendMode('per-customer')}
                                        title="Compose &amp; send a dunning e-mail"
                                        className="inline-flex items-center gap-1 rounded-md bg-rose-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-rose-700"
                                    >
                                        <Mail className="h-3.5 w-3.5" /> Send reminder
                                    </button>
                                )}
                                <button
                                    onClick={() => setSendMode('combined')}
                                    title="Internal e-mail about this item (e.g. to the responsible sales manager)"
                                    className="inline-flex items-center gap-1 rounded-md border border-indigo-300 bg-white px-2.5 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50"
                                >
                                    <Mail className="h-3.5 w-3.5" /> Internal e-mail
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Action history */}
                    <div className="mt-5">
                        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Action history</h4>
                        {loading ? (
                            <div className="flex justify-center py-4 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /></div>
                        ) : actions.length === 0 ? (
                            <p className="text-xs text-slate-400">No actions recorded yet.</p>
                        ) : (
                            <div className="space-y-2">
                                {actions.map(a => {
                                    const Icon = ACTION_ICON[a.ActionType] ?? ClipboardList;
                                    const closable = canEdit && a.Status === 'Open' && CLOSABLE_STATUSES[a.ActionType];
                                    return (
                                        <div key={a.Id} className="rounded border border-slate-200 p-2 text-sm">
                                            <div className="flex items-center justify-between">
                                                <span className="flex items-center gap-1.5 font-medium text-slate-700">
                                                    <Icon className="h-3.5 w-3.5 text-slate-400" />
                                                    {a.ActionType}{a.DunningLevel ? ` L${a.DunningLevel}` : ''}
                                                </span>
                                                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(a.Status)}`}>{a.Status}</span>
                                            </div>
                                            <div className="mt-1 text-xs text-slate-500">
                                                {a.PromiseDate && <>Promise: {formatDate(a.PromiseDate)}{a.PromiseAmount != null ? ` · ${formatMoney(a.PromiseAmount, item.Currency)}` : ''} · </>}
                                                {a.DueAt && <>Due: {formatDate(a.DueAt)} · </>}
                                                {a.CreatedBy} · {formatDateTime(a.CreatedAt)}
                                            </div>
                                            {a.Notes && <p className="mt-1 whitespace-pre-wrap text-xs text-slate-600">{a.Notes}</p>}
                                            {closable && (
                                                <div className="mt-1.5 flex gap-1">
                                                    {CLOSABLE_STATUSES[a.ActionType].map(s => (
                                                        <button
                                                            key={s}
                                                            onClick={() => resolveAction(a, s)}
                                                            className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-0.5 text-xs text-slate-600 hover:bg-slate-50"
                                                        >
                                                            {s === 'Cancelled' ? <Ban className="h-3 w-3" /> : <Check className="h-3 w-3" />} {s}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Comments */}
                    <div className="mt-5 border-t border-slate-100 pt-4">
                        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Comments</h4>
                        <ArCommentsThread entityType="Invoice" entityRef={item.ItemKey} canEdit={canEdit} onChanged={onChanged} />
                    </div>
                </div>
            </div>

            {showAction && (
                <ActionModal
                    item={item}
                    canEdit={canEdit}
                    initialType={showAction}
                    onClose={() => setShowAction(null)}
                    onSaved={afterAction}
                />
            )}

            {sendMode && (
                <SendReminderModal
                    items={[item]}
                    initialLevel={item.NextLevel || item.SuggestedLevel || 1}
                    mode={sendMode}
                    companies={companies}
                    canEdit={canEdit}
                    onClose={() => setSendMode(null)}
                    onSent={afterAction}
                />
            )}
        </div>
    );
}

function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
    return (
        <div className={className}>
            <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
            <div className="text-slate-700">{children}</div>
        </div>
    );
}

function QuickBtn({ icon: Icon, label, onClick }: { icon: typeof CalendarClock; label: string; onClick: () => void }) {
    return (
        <button onClick={onClick} className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">
            <Icon className="h-3.5 w-3.5" /> {label}
        </button>
    );
}

function statusClass(status: string): string {
    switch (status) {
        case 'Open': return 'bg-sky-100 text-sky-700';
        case 'Done':
        case 'Kept': return 'bg-emerald-100 text-emerald-700';
        case 'Broken': return 'bg-rose-100 text-rose-700';
        case 'Cancelled': return 'bg-slate-100 text-slate-500';
        default: return 'bg-slate-100 text-slate-600';
    }
}
